import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Slice de contratos de empleado (histórico + activo).
// Endpoint base: /api/employeeContract
//
// Cache por empleado ({ [employeeId]: contracts[] }) para que la pestaña
// de contratos pueda mostrar histórico sin re-fetch cada vez que se abre.

const initialState = {
  isLoading: false,
  error: null,
  contractsByEmployee: {},
};

const slice = createSlice({
  name: "employeeContracts",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    stopLoading(state) { state.isLoading = false; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },

    setContracts(state, action) {
      const { employeeId, contracts } = action.payload;
      state.contractsByEmployee[employeeId] = contracts || [];
      state.isLoading = false;
    },
    upsertContract(state, action) {
      const c = action.payload;
      const list = state.contractsByEmployee[c.employeeId] || [];
      // Al crear/actualizar, marcar los otros del mismo empleado como
      // inactivos si el nuevo entra como activo (política backend).
      const wasActiveIdx = list.findIndex((x) => x.id === c.id);
      let updated;
      if (wasActiveIdx >= 0) {
        updated = list.map((x) => (x.id === c.id ? c : x));
      } else {
        updated = [c, ...list];
      }
      if (c.isActive) {
        updated = updated.map((x) => (x.id === c.id ? x : { ...x, isActive: false }));
      }
      state.contractsByEmployee[c.employeeId] = updated;
    },
    removeContract(state, action) {
      const { employeeId, id } = action.payload;
      const list = state.contractsByEmployee[employeeId] || [];
      state.contractsByEmployee[employeeId] = list.filter((x) => x.id !== id);
    },
  },
});

export default slice.reducer;
export const { setContracts, upsertContract, removeContract } = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getEmployeeContracts(employeeId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(`/employeeContract/byEmployee/${employeeId}`);
      dispatch(slice.actions.setContracts({ employeeId, contracts: res.data || [] }));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error cargando contratos"));
      throw err;
    }
  };
}

export function createEmployeeContract(payload) {
  return async (dispatch) => {
    const res = await axios.post("/employeeContract", payload);
    dispatch(slice.actions.upsertContract(res.data));
    // Refresca lista completa para reflejar terminación del anterior.
    if (payload?.employeeId) await dispatch(getEmployeeContracts(payload.employeeId));
    return res.data;
  };
}

export function updateEmployeeContract(payload) {
  return async (dispatch) => {
    const res = await axios.put("/employeeContract", payload);
    dispatch(slice.actions.upsertContract(res.data));
    return res.data;
  };
}

export function terminateEmployeeContract(payload) {
  return async (dispatch) => {
    const res = await axios.post("/employeeContract/terminate", payload);
    dispatch(slice.actions.upsertContract(res.data));
    return res.data;
  };
}

export function deleteEmployeeContract(employeeId, id) {
  return async (dispatch) => {
    await axios.delete(`/employeeContract/${id}`);
    dispatch(slice.actions.removeContract({ employeeId, id }));
  };
}
